import React from 'react'
import { motion, isValidMotionProp  } from 'framer-motion'
import {chakra, Button, Stack, Text, Box, Flex, shouldForwardProp} from '@chakra-ui/react'
import { useMediaQuery } from 'react-responsive';

const MotionMuiBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

export const NUM_QUESTIONS_TO_ASK = 7
export const faceUpY = 0
export const faceDownY = 180
export const cardWidth = { base: '286px', md: '388px' }
export const cardHeight = {base: '410px', md: '553px' }
export const goldColor = ' #b7870c'
export const quizFgColor = '#FFE8A5'
const offsetUtil = (arr, amount) => arr.map((x) => x + amount)
const useVariants = (type, yOffset, zOffset) => {
  const zValues = offsetUtil([0, 15, 180, 200, 180, 12, 3, 5, 3], zOffset)
  const yValues = offsetUtil([0, -50, 0, 0], yOffset)

  const questionVariants = {
    question: {
      rotateY: 0,
      y: yOffset,
    },
    answer: {
      rotateY: [0, -5, -175, -180, -180],
      y: yValues,
      z: zValues,
    },
    out: {
      z: offsetUtil([0, 80, 100], zOffset),
      y: yOffset,
      x: 2100,
      transition: { type: 'spring', stiffness: 10 },
      rotateY: -180,
    },
  }

  const answerVariants = {
    question: {
      rotateY: 180,
    },
    answer: {
      rotateY: offsetUtil(questionVariants.answer.rotateY, 180),
      y: yValues,
      z: zValues,
    },
    out: {
      ...questionVariants.out,
      rotateY: 0,
    },
  }
  return type === 'question' ? questionVariants : answerVariants
}

export const QuizCardSide = ({
  type,
  data,
  onClick,
  index,
  status,
  offset,
  isCorrect,
}) => {
  let smallMobile = useMediaQuery({ maxWidth: 374 });
  const isQuestion = type === 'question'
  const isAnswer = type === 'answer'
  const zOffset = -3 * index
  const yOffset = 4 * index
  const variants = useVariants(type, yOffset, zOffset)
  const questionStatementFontSize = { base: '24px', md: '36px' }
  const answerQuoteFontSize = { xs: '20px', md: '30px' }
  const answerTypeFontSize = {base: '24px',  md: '36px' }

  const cardFgColor = isQuestion ? goldColor : '#b7870c'
  const cardBgColor = isQuestion ? '#030040' : 'white'

  return (
    <MotionMuiBox
      variants={variants}
      animate={status}
      style={{
        x: 0,
        rotateX: 5,
        rotateY: isQuestion ? faceUpY : faceDownY,
        zIndex: 200 - 2 * index - (isQuestion ? 1 : 0),
        ...offset,
      }}
      background={cardBgColor}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        backfaceVisibility: 'hidden',
        borderColor: cardFgColor,
        borderRadius: '15px',
        borderWidth: '1px',
        borderStyle: 'solid',
        overflow: 'hidden',
        // bgcolor: cardBgColor,
        boxShadow: 'rgb(0 0 0 / 36%) 1px 6px 9px -2px',
        pointerEvents: 'auto',
        width: '100%',
        height: '100%',
        maxWidth: smallMobile ? '300px' : cardWidth,
        maxHeight: cardHeight,
      }}
    >
      {/* Inner Flex container */}
      <Box
        id='innerDiv'
        display={'flex'}
        flexDirection={'column'}
        position={'relative'}
        py={4}
        px={5}
        width={'100%'}
        height='100%'
        bg={'inherit'}
      >
        <QuizCardHeader fgColor={cardFgColor} />

        {isAnswer && (
          <Text
            variant='h4'
            fontFamily={'var(--AustinMediumWeb)'}
            sx={{
              color: '#000',
              fontSize: answerTypeFontSize,
              mb: '.2em',
              textAlign: 'left',
            }}
          >
            {isCorrect ? <>✓</> : <>✘</>}{' '}
            {data.type === 'Fact' ? 'Fact' : 'Myth'}
          </Text>
        )}

        <Text
          component='div'
          sx={{
            color: cardFgColor,
            fontSize: isQuestion
              ? questionStatementFontSize
              : answerQuoteFontSize,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            textAlign: 'left',
            fontFamily: 'var(--AustinMediumWeb)',
            overflowY: 'hidden',
            py: 2,
            fontWeight: 400,
            lineHeight: {base: '30px', md: '40px'}
          }}
        >
          {isQuestion && data.statement}
          {isAnswer && (
            <Box fontSize={{base:'20px', md: '30px'}} fontWeight={500}>
              {data.quote}
              <Box
                sx={{
                  fontFamily: 'FedraSansStd-book',
                  fontWeight: '500',
                  mt: 3,
                  // fontSize: '22px',
                }}
              >
                - Sadhguru
              </Box>
            </Box>
          )}
        </Text>

        <Stack
          direction='row'
          spacing={{ xs: 2, sm: 3 }}
          justifyContent='center'
          sx={{ mt: 'auto', pt: 2 }}
        >
          {isAnswer && (
            <QuizCardButton
              onClick={onClick}
              fgColor={cardFgColor}
              sx={{
                '&:hover': {
                  borderColor: goldColor,
                  backgroundColor: '#EDF2F7',
                  color: goldColor,
                },
              }}
            >
              {index === NUM_QUESTIONS_TO_ASK - 1 ? 'RESULTS' : 'NEXT'}
            </QuizCardButton>
          )}
          {isQuestion && (
            <Flex width={'100%'} justifyContent={'center'} gap={'25px'}>
              <QuizCardButton
                fgColor={cardFgColor}
                onClick={() => onClick('Myth')}
              >
                MYTH
              </QuizCardButton>
              <QuizCardButton
                fgColor={cardFgColor}
                onClick={() => onClick('Fact')}
              >
                FACT
              </QuizCardButton>
            </Flex>
          )}
        </Stack>
      </Box>
    </MotionMuiBox>
  )
}

const QuizCardHeader = ({ fgColor }) => {
  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        pb={2}
        alignItems='baseline'
        sx={{ width: '100%' }}
      >
        <Text
          as='h1'
          fontSize={{base:'20px', md: '24px' }}
          fontWeight={500}
          fontFamily={'var(--AustinMediumWeb)'}
          sx={{
            color: fgColor,
            fontWeight: '500',
            textTransform: 'uppercase',
          }}
        >
          KARMA
        </Text>
        <Text
          variant='subtitle1'
          fontFamily={'FedraSansStd-book'}
            fontWeight={'450'}
          sx={{
            color: fgColor,
            fontSize: { base:'14px', md: '18px' },
          }}
        >
          MYTH OR FACT
        </Text>
      </Box>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: fgColor,
          mb: 3,
          mx: -2,
        }}
      />
    </>
  )
}

const QuizCardButton = ({ children, onClick, fgColor, sx = {} }) => (
  <Button
    variant='outlined'
    size='large'
    onClick={onClick}
    fontSize={'18px'}
    fontFamily={'FedraSansStd-book'}
    sx={{
      border: '1px solid',
      borderColor: fgColor,
      borderRadius: '6px',
      color: fgColor,
      height: '50px',
      width: '100px',      
      '&:hover': {
        borderColor: 'darkgoldenrod',
        backgroundColor: '#fff',
      },
      ...sx,
    }}
  >
    {children}
  </Button>
)
